import React, { useMemo, useState, useEffect } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { ArrowLeft, CreditCard, Plus, Repeat, Smartphone, Star, Gift, RotateCcw, FileText, Banknote, CircleDollarSign, Check } from 'lucide-react';
import { FallbackMethodPicker } from '../components/FallbackMethodPicker';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { trackEvent } from '../utils/analytics';
import { formatDateInBusinessTZ } from '../utils/date';
import { WalletAddFunds } from '../components/WalletAddFunds';
import { WalletWithdraw } from '../components/WalletWithdraw';
import { ENDPOINTS } from '../config';
import { useLabels } from '../config/useLabels';
import { savedCardLabel } from '../utils/stripeFee';

type BalanceTotals = {
  total_cents: number;
  withdrawable_cents: number;
  non_withdrawable_cents: number;
};

type BalanceTransactionRow = {
  id: string;
  type: 'credit' | 'debit' | 'refund' | 'withdrawal';
  bucket: 'withdrawable' | 'non_withdrawable';
  amount_cents: number;
  note: string | null;
  source_ref_type: string | null;
  created_at: string;
};

const TX_TYPE_LABEL: Record<BalanceTransactionRow['type'], string> = {
  credit: 'Credit', debit: 'Debit', refund: 'Refund', withdrawal: 'Withdrawal',
};

const TX_SOURCE_ICON: Record<string, React.ReactElement> = {
  stripe_payment:         <CreditCard className="w-4 h-4" />,
  zelle_payment:          <Smartphone className="w-4 h-4" />,
  payment_cart_funding:   <FileText className="w-4 h-4" />,
  purchase_bonus:         <Star className="w-4 h-4" />,
  admin_grant:            <Gift className="w-4 h-4" />,
  admin_refund:           <RotateCcw className="w-4 h-4" />,
  invoice_payment:        <FileText className="w-4 h-4" />,
  invoice_payment_refund: <RotateCcw className="w-4 h-4" />,
  stripe_withdrawal:      <CreditCard className="w-4 h-4" />,
  zelle_withdrawal:       <Smartphone className="w-4 h-4" />,
  cash_withdrawal:        <Banknote className="w-4 h-4" />,
};

const TX_TYPE_FALLBACK_ICON: Record<BalanceTransactionRow['type'], React.ReactElement> = {
  credit:     <CircleDollarSign className="w-4 h-4" />,
  debit:      <FileText className="w-4 h-4" />,
  refund:     <RotateCcw className="w-4 h-4" />,
  withdrawal: <Banknote className="w-4 h-4" />,
};

function txIcon(tx: BalanceTransactionRow): React.ReactElement {
  if (tx.source_ref_type && tx.source_ref_type in TX_SOURCE_ICON) return TX_SOURCE_ICON[tx.source_ref_type];
  return TX_TYPE_FALLBACK_ICON[tx.type];
}

function txDescription(tx: BalanceTransactionRow): string {
  const label = TX_TYPE_LABEL[tx.type];
  if (tx.note) return tx.type === 'withdrawal' ? `${label} · ${tx.note}` : tx.note;
  if (tx.source_ref_type) return `${label} · ${tx.source_ref_type.replace(/_/g, ' ')}`;
  return `${label} · ${tx.bucket === 'withdrawable' ? 'Cash' : 'Bonus'}`;
}

type FallbackPaymentMethodOption = {
  id: string;      // Stripe PM ID (value stored in balance_preferences)
  label: string;
  kind: 'card' | 'bank_account';
  sourceId: string; // setup intent ID for banks; stripe PM ID for cards
  brand?: string | null; // card brand (visa/mastercard/…) for the brand icon
};

type ParentPaymentPrefs = {
  default_payment_method: 'zelle' | 'bank_account' | 'card' | null;
  default_bank_account_setup_intent_id: string | null;
  autopay_enabled: boolean | null;
};


function formatUsdFromCents(cents: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
}

export function Wallet() {
  const { user, isLoading: isAuthLoading } = useAuth();
  const queryClient = useQueryClient();
  const t = useLabels();
  const [searchParams, setSearchParams] = useSearchParams();

  const [showAddFunds, setShowAddFunds] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [autopayEnabledDraft, setAutopayEnabledDraft] = useState<boolean>(false);
  const [fallbackPmIdDraft, setFallbackPmIdDraft] = useState<string>('');
  const [hasTouchedAutopay, setHasTouchedAutopay] = useState<boolean>(false);
  const [hasTouchedFallback, setHasTouchedFallback] = useState<boolean>(false);

  // Handle return from ACH redirect (balance_updated=1 query param)
  useEffect(() => {
    if (searchParams.get('balance_updated') === '1') {
      queryClient.invalidateQueries({ queryKey: ['bhcs-cash-balance'] });
      queryClient.invalidateQueries({ queryKey: ['bhcs-cash-recent-tx'] });
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, queryClient]);

  // Handle return from bank account setup redirect
  useEffect(() => {
    const bankSetupComplete = searchParams.get('bank_setup_complete');
    const setupIntentId = searchParams.get('setup_intent_id');
    if (bankSetupComplete !== 'true' || !setupIntentId) return;

    const finalize = async () => {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const token = sessionData.session?.access_token;
        if (!token) return;

        await fetch(ENDPOINTS.updateBankAccountSetupIntent, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ setupIntentId }),
        });

        queryClient.invalidateQueries({ queryKey: ['bank-account-setup'] });
        queryClient.invalidateQueries({ queryKey: ['bhcs-cash-fallback-methods'] });
      } catch {
        // non-fatal — bank setup was confirmed by Stripe; the intent will be updated next time
      } finally {
        setShowAddFunds(true);
        setSearchParams({}, { replace: true });
      }
    };

    finalize();
  }, [searchParams, setSearchParams, queryClient]);

  const { data: parentId } = useQuery<string | null>({
    queryKey: ['bhcs-cash-parent-id', user?.email],
    queryFn: async () => {
      if (!user?.email) return null;
      const { data, error } = await supabase
        .from('parents')
        .select('id')
        .eq('email', user.email)
        .maybeSingle();
      if (error) throw error;
      return data?.id ?? null;
    },
    enabled: !!user?.email,
  });

  const { data: accountId, isLoading: isAccountLoading } = useQuery<string | null>({
    queryKey: ['bhcs-cash-account-id', parentId],
    queryFn: async () => {
      if (!parentId) return null;
      const { data, error } = await supabase
        .from('balance_accounts')
        .select('id')
        .eq('parent_id', parentId)
        .maybeSingle();
      if (error) throw error;
      return data?.id ?? null;
    },
    enabled: !!parentId,
  });

  const { data: balanceTotals, isLoading: isBalanceLoading } = useQuery<BalanceTotals>({
    queryKey: ['bhcs-cash-balance', accountId],
    queryFn: async () => {
      if (!accountId) {
        return { total_cents: 0, withdrawable_cents: 0, non_withdrawable_cents: 0 };
      }
      const { data, error } = await supabase.rpc('get_balance', { p_account_id: accountId });
      if (error) throw error;
      const row = data?.[0];
      return {
        total_cents: row?.total_cents ?? 0,
        withdrawable_cents: row?.withdrawable_cents ?? 0,
        non_withdrawable_cents: row?.non_withdrawable_cents ?? 0,
      };
    },
    enabled: !isAuthLoading,
  });

  const { data: preferences } = useQuery<{
    autopay_enabled: boolean;
    autopay_fallback_payment_method_id: string | null;
  } | null>({
    queryKey: ['bhcs-cash-preferences', accountId],
    queryFn: async () => {
      if (!accountId) return null;
      const { data, error } = await supabase
        .from('balance_preferences')
        .select('autopay_enabled, autopay_fallback_payment_method_id')
        .eq('account_id', accountId)
        .maybeSingle();
      if (error) throw error;
      return data ?? null;
    },
    enabled: !!accountId,
    staleTime: 0,
  });

  type AchPendingRow = { id: string; amount_cents: number; status: 'processing' | 'failed'; created_at: string };

  const { data: achPendingRows } = useQuery<AchPendingRow[]>({
    queryKey: ['bhcs-cash-ach-pending', parentId],
    queryFn: async () => {
      if (!parentId) return [];
      const { data, error } = await supabase
        .from('balance_ach_pending_purchases')
        .select('id, amount_cents, status, created_at')
        .eq('parent_id', parentId)
        .in('status', ['processing', 'failed'])
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as AchPendingRow[];
    },
    enabled: !!parentId,
  });

  const { data: recentTransactions } = useQuery<BalanceTransactionRow[]>({
    queryKey: ['bhcs-cash-recent-tx', accountId],
    queryFn: async () => {
      if (!accountId) return [];
      const { data, error } = await supabase
        .from('balance_transactions')
        .select('id, type, bucket, amount_cents, note, source_ref_type, created_at')
        .eq('account_id', accountId)
        .order('created_at', { ascending: false })
        .limit(5);
      if (error) throw error;
      return (data ?? []) as BalanceTransactionRow[];
    },
    enabled: !!accountId,
  });

  const { data: fallbackOptions } = useQuery<FallbackPaymentMethodOption[]>({
    queryKey: ['bhcs-cash-fallback-methods', parentId],
    queryFn: async () => {
      if (!parentId) return [];

      const [{ data: bankRows, error: bankErr }, { data: cardRows, error: cardErr }] = await Promise.all([
        supabase
          .from('bank_account_setup_intents')
          .select('id, bank_name, last4, status, is_active, payment_method_id')
          .eq('parent_id', parentId)
          .eq('is_active', true)
          .eq('status', 'succeeded')
          .not('payment_method_id', 'is', null),
        supabase
          .from('card_payment_methods')
          .select('id, brand, last4, is_active, stripe_payment_method_id')
          .eq('parent_id', parentId)
          .eq('is_active', true),
      ]);

      if (bankErr) throw bankErr;
      if (cardErr) throw cardErr;

      const bankOptions: FallbackPaymentMethodOption[] = (bankRows ?? []).flatMap((r) => {
        const paymentMethodId = (r as { payment_method_id: string | null }).payment_method_id;
        if (!paymentMethodId) return [];
        const bankName = (r as { bank_name: string | null }).bank_name ?? 'Bank';
        const last4 = (r as { last4: string | null }).last4 ?? '••••';
        const setupIntentId = (r as { id: string }).id;
        return [{ id: paymentMethodId, kind: 'bank_account', label: `${bankName} •••• ${last4} (ACH)`, sourceId: setupIntentId }];
      });

      const cardOptions: FallbackPaymentMethodOption[] = (cardRows ?? []).flatMap((r) => {
        const stripeId = (r as { stripe_payment_method_id: string }).stripe_payment_method_id;
        if (!stripeId) return [];
        const brand = (r as { brand: string | null }).brand;
        const last4 = (r as { last4: string | null }).last4;
        return [{ id: stripeId, kind: 'card', label: savedCardLabel(brand, last4), sourceId: stripeId, brand }];
      });

      return [...bankOptions, ...cardOptions];
    },
    enabled: !!parentId,
    staleTime: 0,
  });

  const { data: parentPrefs } = useQuery<ParentPaymentPrefs | null>({
    queryKey: ['bhcs-cash-parent-prefs', parentId],
    queryFn: async () => {
      if (!parentId) return null;
      const { data, error } = await supabase
        .from('parent_payment_preferences')
        .select('default_payment_method, default_bank_account_setup_intent_id, autopay_enabled')
        .eq('parent_id', parentId)
        .maybeSingle();
      if (error) throw error;
      return data as ParentPaymentPrefs | null;
    },
    enabled: !!parentId,
    staleTime: 0,
  });

  const upsertPreferences = useMutation({
    mutationFn: async (input: { autopay_enabled: boolean; autopay_fallback_payment_method_id: string | null }) => {
      if (!parentId) throw new Error('Parent record not found');

      let ensuredAccountId = accountId;
      if (!ensuredAccountId) {
        const { data: created, error: createErr } = await supabase
          .from('balance_accounts')
          .insert({ parent_id: parentId })
          .select('id')
          .single();
        if (createErr) throw createErr;
        ensuredAccountId = created.id;
      }

      const { error } = await supabase
        .from('balance_preferences')
        .upsert(
          {
            account_id: ensuredAccountId,
            autopay_enabled: input.autopay_enabled,
            autopay_fallback_payment_method_id: input.autopay_fallback_payment_method_id,
          },
          { onConflict: 'account_id' }
        );
      if (error) throw error;

      // Distinct from `autopay_toggle`, which is invoice autopay on the Account page. This
      // one decides whether a BHCS Cash balance settles invoices by itself and what it falls
      // back to when short — a money-movement setting with no audit trail until now.
      trackEvent({
        eventType: 'payment_preferences_update',
        user,
        userType: 'parent',
        entityType: 'balance_account',
        entityId: ensuredAccountId ?? undefined,
        metadata: {
          scope: 'bhcs_cash_autopay',
          autopay_enabled: input.autopay_enabled,
          previous_autopay_enabled: preferences?.autopay_enabled ?? null,
          fallback_payment_method_set: Boolean(input.autopay_fallback_payment_method_id),
          fallback_changed:
            (input.autopay_fallback_payment_method_id ?? null) !==
            (preferences?.autopay_fallback_payment_method_id ?? null),
        },
      });
      return ensuredAccountId;
    },
    onSuccess: (ensuredAccountId) => {
      queryClient.invalidateQueries({ queryKey: ['bhcs-cash-account-id'] });
      queryClient.invalidateQueries({ queryKey: ['bhcs-cash-preferences', ensuredAccountId] });
    },
  });

  const effectiveAutopayEnabled = preferences?.autopay_enabled ?? false;
  const effectiveFallbackPmId = preferences?.autopay_fallback_payment_method_id ?? '';

  useEffect(() => {
    if (!hasTouchedAutopay) setAutopayEnabledDraft(preferences?.autopay_enabled ?? false);
    if (!hasTouchedFallback) {
      const stored = preferences?.autopay_fallback_payment_method_id ?? '';
      if (stored) {
        setFallbackPmIdDraft(stored);
      } else if (parentPrefs && fallbackOptions?.length) {
        // Pre-select the parent's default payment method when no fallback is saved yet,
        // skipping Zelle (only card/bank options are in fallbackOptions).
        let derived = '';
        if (
          parentPrefs.default_payment_method === 'bank_account' &&
          parentPrefs.default_bank_account_setup_intent_id
        ) {
          const match = fallbackOptions.find(
            (o) => o.sourceId === parentPrefs.default_bank_account_setup_intent_id
          );
          if (match) derived = match.id;
        } else if (parentPrefs.default_payment_method === 'card') {
          const firstCard = fallbackOptions.find((o) => o.kind === 'card');
          if (firstCard) derived = firstCard.id;
        }
        if (derived) setFallbackPmIdDraft(derived);
      }
    }
  }, [preferences, parentPrefs, fallbackOptions, hasTouchedAutopay, hasTouchedFallback]);

  const derivedFallbackValue = useMemo(
    () => (hasTouchedFallback ? fallbackPmIdDraft : effectiveFallbackPmId),
    [fallbackPmIdDraft, effectiveFallbackPmId, hasTouchedFallback]
  );

  const derivedAutopayEnabled = useMemo(
    () => (hasTouchedAutopay ? autopayEnabledDraft : effectiveAutopayEnabled),
    [autopayEnabledDraft, effectiveAutopayEnabled, hasTouchedAutopay]
  );

  if (isAuthLoading || isAccountLoading) {
    return <div className="py-10 text-center text-gray-600">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Persist preferences immediately on change (auto-save) so users don't have to remember
  // to click a separate Save button. Callers pass the explicit next values to avoid relying
  // on derived state that only updates on the following render.
  const persistPreferences = (autopayEnabled: boolean, fallbackId: string) => {
    const normalizedFallback = fallbackId.trim() ? fallbackId.trim() : null;
    upsertPreferences.mutate({
      autopay_enabled: autopayEnabled,
      autopay_fallback_payment_method_id: normalizedFallback,
    });
  };

  const handleAddFundsSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['bhcs-cash-balance'] });
    queryClient.invalidateQueries({ queryKey: ['bhcs-cash-recent-tx'] });
  };

  // Invalidate all wallet queries on close so that:
  // - card: balance reflects the webhook credit (which ran while user read the success screen)
  // - ACH: the processing banner appears immediately (ach-pending was never invalidated on success)
  const handleCloseAddFunds = () => {
    queryClient.invalidateQueries({ queryKey: ['bhcs-cash-balance'] });
    queryClient.invalidateQueries({ queryKey: ['bhcs-cash-recent-tx'] });
    queryClient.invalidateQueries({ queryKey: ['bhcs-cash-ach-pending'] });
    setShowAddFunds(false);
  };

  if (showAddFunds && parentId) {
    return (
      <div className="p-3 sm:p-4">
        <div className="flex items-center mb-4">
          <button
            type="button"
            onClick={handleCloseAddFunds}
            className="mr-3 text-gray-500 hover:text-gray-700"
            aria-label="Back to BHCS Cash"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-lg sm:text-xl font-semibold text-gray-900 flex items-center">
            <CurrencyDollarIcon className="w-5 h-5 mr-2 text-gray-700" />
            {t('action.addFunds')}
          </h1>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <WalletAddFunds
            parentId={parentId}
            currentBalanceCents={balanceTotals?.total_cents ?? 0}
            onSuccess={handleAddFundsSuccess}
            onCancel={handleCloseAddFunds}
          />
        </div>
      </div>
    );
  }

  if (showWithdraw) {
    return (
      <div className="p-3 sm:p-4">
        <div className="flex items-center mb-4">
          <button
            type="button"
            onClick={() => setShowWithdraw(false)}
            className="mr-3 text-gray-500 hover:text-gray-700"
            aria-label="Back to BHCS Cash"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-lg sm:text-xl font-semibold text-gray-900 flex items-center">
            <CurrencyDollarIcon className="w-5 h-5 mr-2 text-gray-700" />
            {t('header.withdrawFunds')}
          </h1>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <WalletWithdraw
            withdrawableCents={balanceTotals?.withdrawable_cents ?? 0}
            nonWithdrawableCents={balanceTotals?.non_withdrawable_cents ?? 0}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4">
      <div className="flex items-center justify-between mb-4">
          <h1 className="text-lg sm:text-xl font-semibold text-gray-900 flex items-center">
            <CurrencyDollarIcon className="w-5 h-5 mr-2 text-gray-700" />
            BHCS Cash
          </h1>
          <div className="flex items-center gap-4">
            <Link to="/payments" className="text-sm text-blue-600 hover:text-blue-800">
              {t('nav.payments')}
            </Link>
            <Link to="/account" className="text-sm text-blue-600 hover:text-blue-800">
              {t('nav.account')}
            </Link>
          </div>
        </div>

        {/* Balance card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <p className="text-sm text-gray-600">{t('header.totalBalance')}</p>
          <div className="mt-1 flex items-end gap-3 flex-wrap">
            <p className="text-3xl font-semibold text-gray-900" data-testid="bhcs-cash-total">
              {isBalanceLoading ? '—' : formatUsdFromCents(balanceTotals?.total_cents ?? 0)}
            </p>
            <div className="flex gap-2">
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Cash: {isBalanceLoading ? '—' : formatUsdFromCents(balanceTotals?.withdrawable_cents ?? 0)}
              </span>
              {(isBalanceLoading || (balanceTotals?.non_withdrawable_cents ?? 0) > 0) && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                  Bonus: {isBalanceLoading ? '—' : formatUsdFromCents(balanceTotals?.non_withdrawable_cents ?? 0)}
                </span>
              )}
            </div>
          </div>
          <p className="mt-3 text-xs text-gray-500">
            Bonus credit is non-withdrawable and can only be used for BHCS invoices.
          </p>
          <div className="mt-4 flex gap-3">
            <button
              type="button"
              onClick={() => setShowAddFunds(true)}
              disabled={!parentId}
              className="inline-flex items-center px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="bhcs-cash-add-funds-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              {t('action.addFunds')}
            </button>
            <button
              type="button"
              onClick={() => setShowWithdraw(true)}
              disabled={!parentId}
              className="inline-flex items-center px-4 py-2 rounded-md border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="bhcs-cash-withdraw-btn"
            >
              {t('action.withdraw')}
            </button>
          </div>
        </div>

        {/* ACH processing / failed banners */}
        {(achPendingRows ?? []).map((row) => (
          row.status === 'processing' ? (
            <div key={row.id} className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800" data-testid="ach-processing-banner">
              ACH payment of {formatUsdFromCents(row.amount_cents)} submitted on {formatDateInBusinessTZ(row.created_at)} is processing — funds typically arrive in 3–5 business days.
            </div>
          ) : (
            <div key={row.id} className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" data-testid="ach-failed-banner">
              ACH payment of {formatUsdFromCents(row.amount_cents)} submitted on {formatDateInBusinessTZ(row.created_at)} did not go through. Please try adding funds again.
            </div>
          )
        ))}

        {/* Payment preferences */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="mb-4 flex items-center justify-between gap-3">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center">
              <Repeat className="w-5 h-5 mr-2 text-gray-600" />
              {t('header.paymentPreferences')}
            </h2>
            <span aria-live="polite" className="text-xs shrink-0" data-testid="bhcs-cash-save-status">
              {upsertPreferences.isPending ? (
                <span className="inline-flex items-center gap-1.5 text-gray-500">
                  <span className="w-3.5 h-3.5 border-2 border-gray-300 border-t-gray-500 rounded-full animate-spin" />
                  Saving…
                </span>
              ) : upsertPreferences.isError ? (
                <span className="text-red-600">Couldn&apos;t save — try again</span>
              ) : upsertPreferences.isSuccess ? (
                <span className="inline-flex items-center gap-1 text-green-600">
                  <Check className="w-3.5 h-3.5" />
                  Saved
                </span>
              ) : null}
            </span>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 rounded-lg border border-gray-200">
              <div>
                <p className="font-medium text-gray-900">Use BHCS Cash first</p>
                <p className="text-sm text-gray-600">
                  Apply BHCS Cash automatically when you pay an invoice at checkout, or when Auto Pay pays it for you. Your fallback method covers any remaining balance.
                </p>
                {/* The autopay batch only picks up families with invoice Auto Pay on, so with it
                    off this switch only ever acts at checkout. Loading (undefined) says nothing. */}
                {derivedAutopayEnabled && parentPrefs !== undefined && !parentPrefs?.autopay_enabled && (
                  <p className="mt-2 text-sm text-amber-700" data-testid="bhcs-cash-invoice-autopay-off">
                    Auto Pay is off, so BHCS Cash is only applied when you pay at checkout.{' '}
                    <Link to="/account#payment-settings" className="font-medium underline hover:text-amber-900">
                      Turn on Auto Pay
                    </Link>
                  </p>
                )}
              </div>
              <button
                onClick={() => {
                  const next = !derivedAutopayEnabled;
                  setHasTouchedAutopay(true);
                  setAutopayEnabledDraft(next);
                  persistPreferences(next, derivedFallbackValue);
                }}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  derivedAutopayEnabled ? 'bg-blue-600' : 'bg-gray-200'
                }`}
                data-testid="bhcs-cash-autopay-toggle"
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    derivedAutopayEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div className="p-4 rounded-lg border border-gray-200">
              <label className="block text-sm font-medium text-gray-900 mb-2">
                {t('label.fallbackPaymentMethod')}
              </label>
              <FallbackMethodPicker
                value={derivedFallbackValue}
                options={fallbackOptions ?? []}
                testId="bhcs-cash-fallback-select"
                onChange={(id) => {
                  setHasTouchedFallback(true);
                  setFallbackPmIdDraft(id);
                  persistPreferences(derivedAutopayEnabled, id);
                }}
              />
              <p className="mt-2 text-xs text-gray-600">
                This method is only used when your BHCS Cash balance is not enough to cover an invoice. Changes are saved automatically.
              </p>
              <div className="mt-3">
                <Link
                  to="/account"
                  className="inline-flex items-center px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  {t('action.managePaymentMethods')}
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Recent activity */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">{t('header.recentActivity')}</h2>
            <Link to="/wallet/history" className="text-xs text-blue-600 hover:text-blue-800">
              View full history →
            </Link>
          </div>
          {(recentTransactions ?? []).length === 0 ? (
            <p className="text-sm text-gray-600">No BHCS Cash activity yet.</p>
          ) : (
            <div className="-mx-6 divide-y divide-gray-100">
              {(recentTransactions ?? []).map((tx) => {
                const isDebit = tx.type === 'debit' || tx.type === 'withdrawal';
                const sign = isDebit ? '-' : '+';
                const amount = `${sign}${formatUsdFromCents(Math.abs(tx.amount_cents))}`;
                return (
                  <div key={tx.id} className="flex items-center px-6 py-3 gap-3">
                    <span className="flex-shrink-0 text-gray-400" aria-label={TX_TYPE_LABEL[tx.type]}>
                      {txIcon(tx)}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{txDescription(tx)}</p>
                      <p className="text-xs text-gray-500">{formatDateInBusinessTZ(tx.created_at)}</p>
                    </div>
                    <p className={`flex-shrink-0 text-sm font-semibold ${isDebit ? 'text-red-600' : 'text-green-700'}`}>
                      {amount}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
    </div>
  );
}
